import {
    type TreeNode,
    type TreePatch,
    type TreeSnapshot,
    type TreeType,
    type UndoRecord,
    type PatchQueueEntry,
    type DomChangeEvent,
    type DomChangeEventType,
    type PatchAckResult,
    type VersionedValue,
    type NodeStyle,
    type TreeFont,
    type ForeignChildrenEvent,
} from "./Types.ts";
import { DomTree } from "./DomTree.ts";
import { UndoStack } from "./UndoStack.ts";
import { PatchQueue } from "./PatchQueue.ts";
import { parseHTMLString, parseElement } from "./HtmlParser.ts";

// ─── DomSession ───────────────────────────────────────────────────────────────
//
// The public API that editor components and the SignalR integration layer use.
// Wires DomTree + UndoStack + PatchQueue together.
//
// Data flow:
//
//   Iframe (user edit)  →  capture(patches)  →  returns patches to send
//       ↑                                               ↓
//       │                                        window.sendPatch
//       │
//   tree.on("change") → iframe bridge applies atomic DOM mutations
//
//   window.onReceivePatch  →  onReceivePatch(patch)  →  tree updated
//   window.onPatchRejected →  onPatchRejected(patch) →  tree rolled back

export class DomSession {
    public readonly tree: DomTree;

    public readonly undoStack: UndoStack;
    public readonly patchQueue: PatchQueue;
    private readonly userId: string;

    // ─── Batch / transaction state ────────────────────────────────────────────
    // While a batch is open every capture() call accumulates patches here
    // instead of pushing an UndoRecord. endBatch() commits the whole lot as
    // one record so the user can undo everything with a single Ctrl+Z.
    private _batchActive = false;
    private _batchForward: TreePatch[] = [];
    private _batchInverse: TreePatch[] = [];

    // Listeners for the "patchRejected" session-level event.
    private readonly rejectionListeners: Set<(clientId: string, result: PatchAckResult, message: string) => void>;

    // Listeners notified whenever patches are ready to forward to the server.
    // Fired by capture(), captureWithInverses(), endBatch(), undo(), redo().
    private readonly sendListeners: Set<(patches: TreePatch[]) => void>;

    // Optional handler called before an undo-of-add fires remove(X) when X has foreign children.
    private foreignChildrenHandler: ((event: ForeignChildrenEvent) => "accept" | "reject" | "discard") | null = null;

    constructor(treeId: string, userId: string) {
        this.tree = new DomTree(treeId);
        this.undoStack = new UndoStack();
        this.patchQueue = new PatchQueue();
        this.userId = userId;
        this.rejectionListeners = new Set();
        this.sendListeners = new Set();
    }

    // ─── Snapshot / HTML loading ──────────────────────────────────────────────

    /**
     * Loads a full page snapshot received from the server.
     * Replaces all tree content, clears undo history, and drops any in-flight patches.
     */
    public loadSnapshot(snapshot: TreeSnapshot): void {
        console.log(snapshot);
        this.tree.loadFromSnapshot(snapshot);
        this.undoStack.clear();
        this.patchQueue.clear();
    }

    /**
     * Converts the server's flat Node array into a TreeSnapshot and loads it.
     * Use this instead of loadSnapshot when working directly with the REST snapshot response.
     */
    public loadFromServerNodes(
        page: { id: string; pageType: string; seq: number },
        nodes: ServerNode[],
    ): void {
        this.loadSnapshot(_flatNodesToSnapshot(page, nodes));
    }

    /**
     * Parses an HTML string and replaces the current tree content.
     * Useful when creating a new page from a template or pasting HTML.
     * Clears undo history and in-flight patches.
     */
    public loadFromHTML(html: string): void {
        const children = parseHTMLString(html, this.userId);
        this.replaceRootChildren(children);
    }

    /**
     * Converts a live DOM Element (and its descendants) and replaces the current
     * tree content. Clears undo history and in-flight patches.
     */
    public loadFromElement(element: Element): void {
        const rootNode = parseElement(element, this.userId);
        this.replaceRootChildren([rootNode]);
    }

    private replaceRootChildren(children: TreeNode[]): void {
        // Remove all existing children from the root.
        for (const child of [...this.tree.root.children]) {
            this.tree.removeNode(child.id);
        }
        // Add the new children in order.
        let previousSiblingId: string | undefined = undefined;
        for (const child of children) {
            this.tree.addNode(this.tree.root.id, child, previousSiblingId, undefined);
            previousSiblingId = child.id;
        }
        this.undoStack.clear();
        this.patchQueue.clear();
    }

    // ─── Local mutations ──────────────────────────────────────────────────────

    /**
     * The primary mutation entry point for the editor.
     *
     * Steps:
     *   1. Reads the current tree state to build inverse patches (before mutation).
     *   2. Pushes an UndoRecord onto the undo stack.
     *   3. Assigns a fresh UUID clientId to each patch.
     *   4. Applies each patch optimistically to the tree.
     *   5. Enqueues each patch in the PatchQueue.
     *   6. Returns the clientId-stamped patches for the caller to send via SignalR.
     */
    public capture(patches: TreePatch[]): TreePatch[] {
        return this._capture(patches, false);
    }

    /**
     * Open a batch. Every capture() call until endBatch() accumulates patches
     * into a single pending group. No UndoRecord is pushed until endBatch().
     * This lets disparate helpers (e.g. insert element + insert deps) share
     * one undo record without needing to know about each other.
     *
     * Nesting is not supported — calling beginBatch() while a batch is already
     * open resets the accumulator.
     */
    public beginBatch(): void {
        this._batchActive = true;
        this._batchForward = [];
        this._batchInverse = [];
    }

    /**
     * Close the batch and push a single UndoRecord for everything captured
     * since beginBatch(). Returns all accumulated patches so the caller can
     * forward them to the server in one shot.
     */
    public endBatch(): TreePatch[] {
        if (!this._batchActive) return [];
        this._batchActive = false;

        const forward = this._batchForward;
        const inverse = this._batchInverse;
        this._batchForward = [];
        this._batchInverse = [];

        if (forward.length === 0) return [];

        this.undoStack.push({
            clientIds: forward.map(p => p.clientId),
            forwardPatches: forward,
            inversePatches: inverse,
        });
        this._emitSend(forward);
        return forward;
    }

    /**
     * Same as capture() but suppresses IframeBridge change events for the
     * duration of the tree mutation. Use this when the DOM is already in the
     * correct state (e.g. after a contenteditable text edit) so the bridge
     * does not destroy and recreate the element that the user is editing.
     */
    public capturePreApplied(patches: TreePatch[]): TreePatch[] {
        this.bridgeSuppressed = true;
        const result = this._capture(patches, false);
        this.bridgeSuppressed = false;
        return result;
    }

    /**
     * Checked by IframeBridge.handleChange to suppress DOM mutations
     * while capturePreApplied() is running.
     */
    public bridgeSuppressed = false;

    private _capture(patches: TreePatch[], _unused: boolean): TreePatch[] {
        // Build inverse patches BEFORE mutation — they capture current state.
        const inversePatches = this.buildInversePatches(patches);

        const clientIds = patches.map(() => this.generateClientId());
        const stampedPatches = patches.map((patch, index) => ({
            ...patch,
            clientId: clientIds[index],
        }));

        // Apply patches one by one. Only record patches that the tree accepted.
        // Rejected patches already emitted "validationFailed" via the tree — caller
        // subscribes to that event to show the user an error message.
        const appliedIndices: number[] = [];

        for (let i = 0; i < stampedPatches.length; i++) {
            const result = this.tree.applyPatch(stampedPatches[i]);
            if (result !== null) {
                this.patchQueue.enqueue(stampedPatches[i]);
                appliedIndices.push(i);
            }
        }

        const sent = appliedIndices.map(i => stampedPatches[i]);

        if (appliedIndices.length > 0) {
            const record: UndoRecord = {
                clientIds: appliedIndices.map(i => clientIds[i]),
                forwardPatches: sent,
                inversePatches: appliedIndices.map(i => inversePatches[i]),
            };
            if (this._batchActive) {
                // Accumulate — UndoRecord is pushed by endBatch()
                this._batchForward.push(...record.forwardPatches);
                this._batchInverse.push(...record.inversePatches);
            } else {
                this.undoStack.push(record);
                this._emitSend(sent); // emit only outside a batch; batch emits via endBatch()
            }
        }

        return sent;
    }

    // ─── Remote patches ───────────────────────────────────────────────────────

    /**
     * Applies patches received from the server that belong to other clients.
     * No undo history is recorded.
     * Used for catchup batches where we know all patches are remote.
     */
    public applyRemote(patches: TreePatch[]): void {
        for (const patch of patches) {
            this.tree.applyPatch(patch);
        }
    }

    /**
     * Applies patches to the tree for live preview (e.g. slider drag).
     * No undo record is created and nothing is enqueued for the server.
     * Follow up with captureWithInverses() once the gesture completes.
     */
    public applyPreview(patches: TreePatch[]): void {
        for (const patch of patches) {
            this.tree.applyPatch({ ...patch, clientId: "_preview", seq: 0 });
        }
    }

    /**
     * Snapshots the inverse of the given patches against the current tree state.
     * Call this BEFORE the first applyPreview() in a gesture to capture the
     * original state for undo.
     */
    public buildInverseFor(patches: TreePatch[]): TreePatch[] {
        return this.buildInversePatches(patches);
    }

    /**
     * Like capture() but uses pre-built inverse patches instead of computing
     * them from the current tree state. Use after applyPreview() so the undo
     * record points back to the state before the preview started.
     */
    public captureWithInverses(patches: TreePatch[], prebuiltInverses: TreePatch[]): TreePatch[] {
        const clientIds = patches.map(() => this.generateClientId());
        const stampedPatches = patches.map((patch, i) => ({ ...patch, clientId: clientIds[i] }));

        const appliedIndices: number[] = [];
        for (let i = 0; i < stampedPatches.length; i++) {
            const result = this.tree.applyPatch(stampedPatches[i]);
            if (result !== null) {
                this.patchQueue.enqueue(stampedPatches[i]);
                appliedIndices.push(i);
            }
        }

        const sent = appliedIndices.map(i => stampedPatches[i]);

        if (appliedIndices.length > 0) {
            const record: UndoRecord = {
                clientIds: appliedIndices.map(i => clientIds[i]),
                forwardPatches: sent,
                inversePatches: appliedIndices.map(i => prebuiltInverses[i] ?? prebuiltInverses[0]),
            };
            this.undoStack.push(record);
            this._emitSend(sent);
        }

        return sent;
    }

    // ─── Server response handlers ─────────────────────────────────────────────

    /**
     * Called with every patch received from SignalR's onReceivePatch.
     *
     * If the patch's clientId is in the pending queue, it is our own patch
     * being confirmed by the server. Otherwise it is a remote patch from
     * another client.
     */
    public onReceivePatch(patch: TreePatch): void {
        const pendingEntry = this.patchQueue.get(patch.clientId);

        if (pendingEntry) {
            this.handleOwnPatchConfirmed(patch);
        } else {
            this.handleRemotePatch(patch);
        }
    }

    /**
     * Called when SignalR's onPatchRejected fires.
     * Automatically rolls back the rejected change in the local tree.
     */
    public onPatchRejected(patch: TreePatch, result: PatchAckResult): void {
        const entry = this.patchQueue.acknowledge(patch.clientId);
        if (!entry) {
            return;
        }

        // For move and deleteFirst-add patches, each subsequent pending patch for
        // the same node was computed against positions that are about to be unwound.
        // Roll them back in reverse-application order first so each inverse sees
        // the correct tree state.
        const type = entry.patch.type;
        if (type === "move" || (type === "add" && entry.patch.deleteFirst)) {
            const nodeId = entry.patch.nodeId;
            const subsequent = this.patchQueue.getAllPending()
                .filter(e => e.patch.nodeId === nodeId);

            for (let i = subsequent.length - 1; i >= 0; i--) {
                const dep = subsequent[i];
                this.patchQueue.acknowledge(dep.clientId);
                const depRecord = this.undoStack.getRecordByClientId(dep.clientId);
                this.rollBackRejectedPatch(dep.patch, depRecord);
                this.undoStack.removeByClientId(dep.clientId);
            }
        }

        // Retrieve the UndoRecord BEFORE removing it so rollback can extract the inverse patch.
        const undoRecord = this.undoStack.getRecordByClientId(patch.clientId);

        this.rollBackRejectedPatch(entry.patch, undoRecord);

        this.undoStack.removeByClientId(patch.clientId);

        const message = patch.resultMessage ?? "";
        for (const listener of this.rejectionListeners) {
            listener(patch.clientId, result, message);
        }
    }

    /**
     * Returns timed-out pending patches so the caller can resend them.
     * Each returned patch keeps its original clientId — the server treats
     * resent patches with the same clientId as idempotent.
     */
    public retryTimedOutPatches(timeoutMs: number): PatchQueueEntry[] {
        const timedOut = this.patchQueue.getTimedOutEntries(timeoutMs);
        for (const entry of timedOut) {
            this.patchQueue.recordRetry(entry.clientId);
        }
        return timedOut;
    }

    // ─── Undo / Redo ──────────────────────────────────────────────────────────
    //
    // Undo and redo produce completely new patches — fresh clientIds, version: 0,
    // seq: 0. The server treats them as ordinary new edits and assigns real values.
    // Other clients receive them as normal property changes.

    public canUndo(): boolean {
        return this.undoStack.canUndo();
    }

    public canRedo(): boolean {
        return this.undoStack.canRedo();
    }

    /**
     * Undoes the last local action.
     * Returns the new patches to send via SignalR, or null if nothing to undo.
     * Returns null without popping if the foreign-children handler returns "reject".
     * Discards the record (without applying) if the handler returns "discard".
     */
    public undo(): TreePatch[] | null {
        const record = this.undoStack.peekUndo();
        if (!record) return null;

        const decision = this.checkForeignChildren(record);
        if (decision === "reject") return null;
        if (decision === "discard") {
            this.undoStack.discardTopUndo();
            return null;
        }

        this.undoStack.undo();
        const patches = this.applyAsNewPatches(record.inversePatches);
        record.pendingClientIds = patches.map(p => p.clientId);
        return patches;
    }

    /**
     * Redoes the last undone action.
     * Returns the new patches to send via SignalR, or null if nothing to redo.
     */
    public redo(): TreePatch[] | null {
        const record = this.undoStack.redo();
        if (!record) return null;
        const patches = this.applyAsNewPatches(record.forwardPatches);
        record.pendingClientIds = patches.map(p => p.clientId);
        return patches;
    }

    /** Register a handler called before an undo-of-add would cascade-delete foreign children. */
    public onForeignChildrenDetected(handler: (event: ForeignChildrenEvent) => "accept" | "reject" | "discard"): void {
        this.foreignChildrenHandler = handler;
    }

    public offForeignChildrenDetected(): void {
        this.foreignChildrenHandler = null;
    }

    // ─── Events ───────────────────────────────────────────────────────────────

    public on(eventType: DomChangeEventType | "change", listener: (event: DomChangeEvent) => void): void {
        this.tree.on(eventType, listener);
    }

    public off(eventType: DomChangeEventType | "change", listener: (event: DomChangeEvent) => void): void {
        this.tree.off(eventType, listener);
    }

    public onPatchRejectedEvent(listener: (clientId: string, result: PatchAckResult, message: string) => void): void {
        this.rejectionListeners.add(listener);
    }

    public offPatchRejectedEvent(listener: (clientId: string, result: PatchAckResult, message: string) => void): void {
        this.rejectionListeners.delete(listener);
    }

    /**
     * Register a listener that is called whenever patches are ready to be
     * forwarded to the server (after capture, undo, redo, or batch end).
     * The listener receives the stamped patches — the same array that would
     * previously have been passed to window.sendPatches by each call site.
     */
    public onPatchesToSend(listener: (patches: TreePatch[]) => void): void {
        this.sendListeners.add(listener);
    }

    public offPatchesToSend(listener: (patches: TreePatch[]) => void): void {
        this.sendListeners.delete(listener);
    }

    private _emitSend(patches: TreePatch[]): void {
        if (patches.length === 0) return;
        for (const listener of this.sendListeners) listener(patches);
        let eventName = window["canvasEventsLabels"].patchesToSend;
        if (eventName) {
            // Serialize Maps → plain objects so the event payload is JSON-safe for SignalR.
            const serializable = patches.map(_serializePatch);
            window.dispatchEvent(new CustomEvent(eventName, {
                detail: { pageId: this.tree.id, patches: serializable },
            }));
        }
    }

    // ─── Private: own-patch confirmation ─────────────────────────────────────

    private handleOwnPatchConfirmed(serverPatch: TreePatch): void {
        this.patchQueue.acknowledge(serverPatch.clientId);

        // For add patches the server returns affectedNodes with real LexoRank positions.
        if (serverPatch.type === "add" && serverPatch.affectedNodes && serverPatch.affectedNodes.length > 0) {
            this.tree.confirmNodePosition(serverPatch.affectedNodes);
        }

        // For move patches the server returns the confirmed position.
        if (serverPatch.type === "move" && serverPatch.position) {
            this.tree.confirmNodePosition(serverPatch.nodeId, serverPatch.position);
        }

        // Suppress bridge events while syncing the server-assigned version so the
        // DOM isn't re-applied (the value is already correct locally).
        if (serverPatch.versionedValue && serverPatch.seq > 0) {
            this.bridgeSuppressed = true;
            this.tree.applyPatch(serverPatch);
            this.bridgeSuppressed = false;
        }

        if (serverPatch.seq > this.tree.seq) {
            this.tree.seq = serverPatch.seq;
        }
    }

    // ─── Private: remote patch handling ──────────────────────────────────────

    private handleRemotePatch(patch: TreePatch): void {
        this.tree.applyPatch(patch);
        // Mirror what handleOwnPatchConfirmed does: confirm the server-assigned positions
        // so remote nodes are sorted correctly and don't sit with position "".
        if (patch.type === "move" && patch.position) {
            this.tree.confirmNodePosition(patch.nodeId, patch.position);
        }
        if (patch.type === "add" && patch.affectedNodes?.length) {
            this.tree.confirmNodePosition(patch.affectedNodes);
        }
    }

    // ─── Private: rollback on rejection ──────────────────────────────────────

    private rollBackRejectedPatch(originalPatch: TreePatch, undoRecord: UndoRecord | null): void {
        // Find the inverse patch corresponding to this specific forward patch.
        // The UndoRecord stores clientIds and inversePatches at matching indices.
        const inversePatch = this.extractInversePatch(originalPatch.clientId, undoRecord);

        switch (originalPatch.type) {
            case "add": {
                if (originalPatch.deleteFirst && inversePatch?.type === "add" && inversePatch.node && inversePatch.parentId) {
                    // deleteFirst add: the old node was destroyed by the forward patch.
                    // Apply the inverse (also a deleteFirst add) to remove the new node
                    // and restore the old content.
                    this.tree.applyPatch({ ...inversePatch, clientId: "" });
                } else {
                    // Plain add: remove the optimistically added node and drop any
                    // pending child patches that depend on it.
                    const subtreeIds = this.collectSubtreeIds(originalPatch.nodeId);
                    this.tree.removeNode(originalPatch.nodeId);
                    this.patchQueue.removeForSubtree(subtreeIds);
                }
                break;
            }

            case "remove": {
                // The server kept the node — restore it from the undo record.
                if (inversePatch && inversePatch.type === "add" && inversePatch.node && inversePatch.parentId) {
                    this.tree.addNode(
                        inversePatch.parentId,
                        inversePatch.node,
                        inversePatch.siblingBeforeId,
                        inversePatch.siblingAfterId,
                    );
                }
                break;
            }

            case "move": {
                if (inversePatch && inversePatch.type === "move" && inversePatch.parentId) {
                    this.tree.moveNode(
                        inversePatch.nodeId,
                        inversePatch.parentId,
                        inversePatch.siblingBeforeId,
                        inversePatch.siblingAfterId,
                    );
                }
                break;
            }

            default: {
                // Property patch — restore the pre-mutation value from the inverse.
                if (inversePatch) {
                    this.tree.applyPatch(inversePatch);
                }
                break;
            }
        }
    }

    /**
     * Locates the inverse patch for a given forward-patch clientId by looking it
     * up in the UndoRecord that was built at capture() time.
     * clientIds[i] corresponds to inversePatches[i].
     */
    private extractInversePatch(clientId: string, undoRecord: UndoRecord | null): TreePatch | null {
        if (!undoRecord) {
            return null;
        }
        const index = undoRecord.clientIds.indexOf(clientId);
        if (index === -1) {
            return null;
        }
        return undoRecord.inversePatches[index] ?? null;
    }

    private collectSubtreeIds(nodeId: string): Set<string> {
        const ids = new Set<string>();
        const collect = (id: string) => {
            const node = this.tree.findNodeById(id);
            if (!node) {
                return;
            }
            ids.add(id);
            for (const child of node.children) {
                collect(child.id);
            }
        };
        collect(nodeId);
        return ids;
    }

    /** Collects all node IDs in a snapshot subtree (not the live tree). */
    private collectSnapshotIds(node: TreeNode, ids: Set<string>): void {
        ids.add(node.id);
        for (const child of node.children ?? []) {
            this.collectSnapshotIds(child, ids);
        }
    }

    /**
     * Checks if any remove inverse in the record would cascade-delete children
     * added by other users. Calls foreignChildrenHandler if registered.
     */
    private checkForeignChildren(record: UndoRecord): "accept" | "reject" | "discard" {
        for (let i = 0; i < record.inversePatches.length; i++) {
            const inverse = record.inversePatches[i];
            if (inverse.type !== "remove") continue;

            const currentNode = this.tree.findNodeById(inverse.nodeId);
            if (!currentNode || currentNode.children.length === 0) continue;

            const originalIds = new Set<string>();
            const forward = record.forwardPatches[i];
            if (forward?.type === "add" && forward.node) {
                this.collectSnapshotIds(forward.node, originalIds);
            }

            const foreignChildren = currentNode.children.filter(c => !originalIds.has(c.id));
            if (foreignChildren.length === 0) continue;

            if (this.foreignChildrenHandler) {
                return this.foreignChildrenHandler({ nodeId: inverse.nodeId, foreignChildren });
            }
        }
        return "accept";
    }

    // ─── Private: undo/redo application ──────────────────────────────────────

    /**
     * Applies a set of patches as completely new changes.
     * Local apply uses currentVersion + 1 to guarantee LWW wins.
     * Returned patches use version: 0, seq: 0 — the server assigns the real value.
     */
    private applyAsNewPatches(patches: TreePatch[]): TreePatch[] {
        const result: TreePatch[] = [];

        for (const patch of patches) {
            const clientId = this.generateClientId();
            // Compute a local version that always beats the current LWW state.
            const localVersion = this.computeWinningVersion(patch);
            const localPatch: TreePatch = {
                ...patch,
                clientId,
                seq: 0,
                versionedValue: patch.versionedValue
                    ? { version: localVersion, seq: 0, value: patch.versionedValue.value }
                    : undefined,
            };
            this.tree.applyPatch(localPatch);
            this.patchQueue.enqueue(localPatch);
            // Return version: 0 to caller — server assigns the canonical version.
            result.push({
                ...localPatch,
                versionedValue: localPatch.versionedValue
                    ? { version: 0, seq: 0, value: localPatch.versionedValue.value }
                    : undefined,
            });
        }

        this._emitSend(result);
        return result;
    }

    /**
     * Returns current property version + 1 so an undo/redo patch always beats
     * the LWW guard. Non-property patches (add/remove/move) return 0.
     */
    private computeWinningVersion(patch: TreePatch): number {
        if (!patch.versionedValue) return 0;
        const node = this.tree.findNodeById(patch.nodeId);
        if (!node) return 0;
        let currentVersion = 0;
        switch (patch.type) {
            case "updateAttribute":
                currentVersion = node.attributes.get(patch.propertyName?.toLowerCase() ?? "")?.version ?? 0;
                break;
            case "updateClass":
                currentVersion = node.classes.get(patch.propertyName ?? "")?.version ?? 0;
                break;
            case "updateText":
                currentVersion = node.text.version;
                break;
            case "updateTagName":
                currentVersion = node.tagName.version;
                break;
            case "updateComment":
                currentVersion = node.comment.version;
                break;
            case "setCustomStyles":
                currentVersion = node.customStyles.get(patch.propertyName ?? "")?.version ?? 0;
                break;
            default:
                return 0;
        }
        return 0;//currentVersion + 1;
    }

    // ─── Private: inverse patch construction ─────────────────────────────────

    /**
     * Reads the current tree state to produce the inverse of each patch.
     * MUST be called BEFORE the patches are applied to the tree.
     */
    private buildInversePatches(patches: TreePatch[]): TreePatch[] {
        return patches.map(patch => this.buildInversePatch(patch));
    }

    private buildInversePatch(patch: TreePatch): TreePatch {
        switch (patch.type) {
            case "add": {
                // For deleteFirst patches the existing node is about to be destroyed.
                // Capture its full subtree now so undo can re-insert it.
                if (patch.deleteFirst) {
                    const oldNode = this.tree.findNodeById(patch.nodeId);
                    if (oldNode) {
                        const parent = this.tree.findNodeById(oldNode.parentId);
                        const nodeIndex = parent
                            ? parent.children.findIndex(c => c.id === patch.nodeId)
                            : -1;
                        const siblingBeforeId =
                            nodeIndex > 0 ? parent!.children[nodeIndex - 1].id : undefined;
                        const siblingAfterId =
                            parent && nodeIndex < parent.children.length - 1
                                ? parent.children[nodeIndex + 1].id
                                : undefined;
                        return {
                            type: "add",
                            clientId: "",
                            nodeId: patch.nodeId,
                            seq: 0,
                            parentId: oldNode.parentId,
                            siblingBeforeId,
                            siblingAfterId,
                            node: oldNode,
                            deleteFirst: true,
                        };
                    }
                }
                return {
                    type: "remove",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                };
            }

            case "remove": {
                const node = this.tree.findNodeById(patch.nodeId);
                if (!node) {
                    return patch;
                }
                const parent = this.tree.findNodeById(node.parentId);
                const nodeIndex = parent ? parent.children.findIndex(child => child.id === patch.nodeId) : -1;
                const siblingBeforeId = nodeIndex > 0 ? parent!.children[nodeIndex - 1].id : undefined;
                const siblingAfterId =
                    parent && nodeIndex < parent.children.length - 1
                        ? parent.children[nodeIndex + 1].id
                        : undefined;

                return {
                    type: "add",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                    parentId: node.parentId,
                    siblingBeforeId,
                    siblingAfterId,
                    node: this.tree.findNodeById(patch.nodeId),
                };
            }

            case "move": {
                const node = this.tree.findNodeById(patch.nodeId);
                if (!node) {
                    return patch;
                }
                const parent = this.tree.findNodeById(node.parentId);
                const nodeIndex = parent ? parent.children.findIndex(child => child.id === patch.nodeId) : -1;
                const siblingBeforeId = nodeIndex > 0 ? parent!.children[nodeIndex - 1].id : undefined;
                const siblingAfterId =
                    parent && nodeIndex < parent.children.length - 1
                        ? parent.children[nodeIndex + 1].id
                        : undefined;

                return {
                    type: "move",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                    parentId: node.parentId,
                    siblingBeforeId,
                    siblingAfterId,
                };
            }

            case "updateAttribute": {
                const node = this.tree.findNodeById(patch.nodeId);
                const propertyName = patch.propertyName?.toLowerCase() ?? "";
                const currentValue = node?.attributes.get(propertyName);
                // Inverse version must beat the forward patch's version after optimistic apply.
                // currentVersion + 1 is wrong if forward.version > currentVersion + 1.
                const attrInverseVersion = (patch.versionedValue?.version ?? 0) + 1;
                return {
                    type: "updateAttribute",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                    propertyName,
                    versionedValue: { version: attrInverseVersion, seq: 0, value: currentValue?.value ?? null },
                };
            }

            case "updateClass": {
                const node = this.tree.findNodeById(patch.nodeId);
                const className = patch.propertyName ?? "";
                const currentValue = node?.classes.get(className);
                const classInverseVersion = (patch.versionedValue?.version ?? 0) + 1;
                return {
                    type: "updateClass",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                    propertyName: className,
                    versionedValue: { version: classInverseVersion, seq: 0, value: currentValue?.value ?? false },
                };
            }

            case "updateText": {
                const node = this.tree.findNodeById(patch.nodeId);
                const textInverseVersion = (patch.versionedValue?.version ?? 0) + 1;
                return {
                    type: "updateText",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                    versionedValue: { version: textInverseVersion, seq: 0, value: node?.text.value ?? null },
                };
            }

            case "updateTagName": {
                const node = this.tree.findNodeById(patch.nodeId);
                const tagInverseVersion = (patch.versionedValue?.version ?? 0) + 1;
                return {
                    type: "updateTagName",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                    versionedValue: { version: tagInverseVersion, seq: 0, value: node?.tagName.value ?? "" },
                };
            }

            case "updateComment": {
                const node = this.tree.findNodeById(patch.nodeId);
                const commentInverseVersion = (patch.versionedValue?.version ?? 0) + 1;
                return {
                    type: "updateComment",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                    versionedValue: { version: commentInverseVersion, seq: 0, value: node?.comment.value ?? null },
                };
            }

            case "setCustomStyles": {
                const node = this.tree.findNodeById(patch.nodeId);
                const key = patch.propertyName ?? "";
                const current = node?.customStyles.get(key);
                const customInverseVersion = (patch.versionedValue?.version ?? 0) + 1;
                return {
                    type: "setCustomStyles",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                    propertyName: key,
                    versionedValue: { version: customInverseVersion, seq: 0, value: current?.value ?? "" },
                };
            }

            case "setBuilderStyle": {
                if (!patch.propertyName || patch.pseudo === undefined || patch.media === undefined || patch.theme === undefined) {
                    return patch;
                }
                const existingStyles = this.tree.builderStyles.get(patch.nodeId) ?? [];
                const existing = existingStyles.find(
                    style =>
                        style.property === patch.propertyName &&
                        style.pseudo === patch.pseudo &&
                        style.media === patch.media &&
                        style.theme === patch.theme,
                );
                if (existing) {
                    return {
                        type: "setBuilderStyle",
                        clientId: "",
                        nodeId: patch.nodeId,
                        seq: 0,
                        propertyName: patch.propertyName,
                        pseudo: patch.pseudo,
                        media: patch.media,
                        theme: patch.theme,
                        versionedValue: { version: existing.version + 1, seq: 0, value: existing.value ?? null },
                    };
                }
                return {
                    type: "removeBuilderStyle",
                    clientId: "",
                    nodeId: patch.nodeId,
                    seq: 0,
                    propertyName: patch.propertyName,
                    pseudo: patch.pseudo,
                    media: patch.media,
                    theme: patch.theme,
                    versionedValue: { version: 0, seq: 0, value: null },
                };
            }

            default: {
                return patch;
            }
        }
    }

    // ─── Private: helpers ─────────────────────────────────────────────────────

    private generateClientId(): string {
        return crypto.randomUUID();
    }

    public static snapshotFromServerNodes(
        page: { id: string; pageType: string; seq: number },
        data: ServerSnapshot,
    ): TreeSnapshot {
        return _flatNodesToSnapshot(page, data.nodes, data.styles ?? [], data.fonts ?? []);
    }
}

// Re-export the types callers need so they only import from DomSession.
export type {
    TreeNode,
    TreePatch,
    TreeSnapshot,
    DomChangeEvent,
    DomChangeEventType,
    PatchAckResult,
    PatchQueueEntry,
} from "./Types.ts";

// ── Server snapshot conversion ────────────────────────────────────────────────

/** Shape of the full GET /page/snapshot response. */
export interface ServerSnapshot {
    nodes: ServerNode[];
    styles: ServerNodeStyle[];
    fonts: ServerFont[];
}

/** Shape of a single node as returned by GET /page/snapshot. */
export interface ServerNode {
    id: string;
    type: string;
    parentId: string | null;
    position: string | null;
    tagName:  { value: string;          version: number } | null;
    text:     { value: string | null;   version: number } | null;
    comment:  { value: string | null;   version: number } | null;
    attributes: Record<string, { value: string | null; version: number }> | null;
    classes:    Record<string, { value: boolean;       version: number }> | null;
    createdUserId: string | null;
    updatedUserId: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ServerNodeStyle {
    nodeId: string;
    property: string;
    value: string | null;
    pseudo: string;
    media: string;
    theme: string;
    version: number;
    seq: number;
    updatedAt: string;
}

export interface ServerFont {
    id: string;
    name: string;
    contentUrl: string;
}

const _PAGE_TYPES: Record<string, TreeType> = {
    document: 'document', template: 'template', component: 'component', layout: 'layout',
};

function _v<T>(s: { value: T; version: number } | null, def: T): VersionedValue<T> {
    return s ? { value: s.value, version: s.version, seq: 0 } : { value: def, version: 0, seq: 0 };
}

function _flatNodesToSnapshot(
    page: { id: string; pageType: string; seq: number },
    nodes: ServerNode[],
    styles: ServerNodeStyle[] = [],
    fonts: ServerFont[] = [],
): TreeSnapshot {
    const nodeMap = new Map<string, TreeNode>();

    for (const n of nodes) {
        nodeMap.set(n.id, {
            id:            n.id,
            type:          (n.type ?? 'element').toLowerCase() as TreeNode['type'],
            parentId:      n.parentId ?? '',
            position:      n.position ?? '',
            tagName:       _v(n.tagName, ''),
            text:          _v(n.text, null),
            comment:       _v(n.comment, null),
            // restoreNodeMaps (called inside loadFromSnapshot) converts these plain objects to Maps.
            attributes:    (n.attributes ?? {}) as unknown as TreeNode['attributes'],
            classes:       (n.classes    ?? {}) as unknown as TreeNode['classes'],
            // Server stores customStyles as a single raw CSS blob; client uses a per-property Map.
            customStyles:  new Map(),
            children:      [],
            createdUserId: n.createdUserId ?? '',
            updatedUserId: n.updatedUserId ?? '',
            createdAt:     n.createdAt ? new Date(n.createdAt).getTime() : Date.now(),
            updatedAt:     n.updatedAt ? new Date(n.updatedAt).getTime() : Date.now(),
        });
    }

    // Attach nodes to their parents; collect page-root nodes (parentId null/empty).
    const pageRoots: TreeNode[] = [];
    for (const node of nodeMap.values()) {
        if (!node.parentId) {
            pageRoots.push(node);
        } else {
            const parent = nodeMap.get(node.parentId);
            if (parent) parent.children.push(node);
        }
    }

    for (const node of nodeMap.values()) {
        if (node.children.length > 1)
            node.children.sort((a, b) => a.position < b.position ? -1 : a.position > b.position ? 1 : 0);
    }

    // The synthetic document root is always present; server-side root nodes
    // (parentId = null) become its children, never the root itself.
    const root: TreeNode = {
        id: 'root', type: 'document', parentId: '', position: '',
        tagName: { value: '', version: 0, seq: 0 },
        text:    { value: null, version: 0, seq: 0 },
        comment: { value: null, version: 0, seq: 0 },
        attributes: new Map(), classes: new Map(), customStyles: new Map(),
        children: pageRoots,
        createdUserId: '', updatedUserId: '', createdAt: Date.now(), updatedAt: Date.now(),
    };
    // Point page-root nodes at the synthetic document root so tree lookups work.
    for (const n of pageRoots) n.parentId = 'root';

    const builderStyles: Record<string, NodeStyle[]> = {};
    for (const s of styles) {
        if (!builderStyles[s.nodeId]) builderStyles[s.nodeId] = [];
        builderStyles[s.nodeId].push({
            nodeId:    s.nodeId,
            property:  s.property,
            value:     s.value ?? undefined,
            pseudo:    s.pseudo,
            media:     s.media,
            theme:     s.theme,
            version:   s.version,
            seq:       s.seq,
            updatedAt: new Date(s.updatedAt).getTime(),
        });
    }

    const fontMap: Record<string, TreeFont> = {};
    for (const f of fonts) {
        fontMap[f.id] = { id: f.id, name: f.name, contentUrl: f.contentUrl };
    }

    return {
        id:            page.id,
        pageType:      _PAGE_TYPES[(page.pageType ?? '').toLowerCase()] ?? 'document',
        version:       0,
        seq:           page.seq ?? 0,
        root,
        builderStyles,
        fonts: fontMap,
    };
}

// Converts a TreePatch to a plain-object form safe for JSON.stringify (Maps → objects).
function _serializePatch(patch: TreePatch): TreePatch {
    if (patch.type === "add" && patch.node) {
        return { ...patch, node: _serializeNode(patch.node) };
    }
    return patch;
}

function _serializeNode(node: TreeNode): TreeNode {
    return {
        ...node,
        attributes:  Object.fromEntries(node.attributes) as unknown as TreeNode["attributes"],
        classes:     Object.fromEntries(node.classes)    as unknown as TreeNode["classes"],
        customStyles: Object.fromEntries(node.customStyles) as unknown as TreeNode["customStyles"],
        children:    node.children.map(_serializeNode),
    };
}

export { DomTree } from "./DomTree.ts";
